import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import "../../../App.css";

const DossierStatCard = ({ label, value }) => (
  <div className="profile-stat-cell dossier-stat-card">
    <div className="profile-stat-label">{label}</div>
    <div className="profile-stat-value dossier-stat-value">{value}</div>
  </div>
);

const formatEventLabel = (type) => {
  switch (type) {
    case "page_view":
      return "Page View";
    case "right_click_attempt":
      return "Right Click Attempt";
    case "devtools_shortcut_block":
      return "Devtools Shortcut Block";
    default:
      return type;
  }
};

const formatTimestamp = (value) => {
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value || "Unknown";
  }
};

const UserDossier = () => {
  const { id } = useParams();
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDossier = async () => {
      try {
        const res = await fetch(`/api/analytics/user/${id}`, {
          credentials: "include",
          cache: "no-store",
        });

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();
        setDossier(data);
      } catch (error) {
        console.error("❌ Failed to load user dossier:", error);
      } finally {
        setLoading(false);
      }
    };

    loadDossier();
  }, [id]);

  const signalClass = useMemo(() => {
    const level = dossier?.signals?.level;
    if (level === "HIGH") return "dossier-signal-high";
    if (level === "WATCH") return "dossier-signal-watch";
    return "dossier-signal-low";
  }, [dossier]);

  if (loading) {
    return (
      <div className="profile-shell">
        <div className="profile-panel dossier-panel">
          <h1 className="profile-title">Loading Dossier...</h1>
        </div>
      </div>
    );
  }

  if (!dossier) {
    return (
      <div className="profile-shell">
        <div className="profile-panel dossier-panel">
          <h1 className="profile-title">Dossier Unavailable</h1>
          <div className="profile-top-actions">
            <Link to="/analytics" className="profile-admin-button">
              RETURN TO ANALYTICS
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const { identity, summary, routes, cycles, events, signals } = dossier;

  return (
    <div className="profile-shell">
      <div className="profile-panel dossier-panel">
        <div className="profile-topbar">
          <div>
            <div className="profile-kicker">OFFICE DOSSIER</div>
            <h1 className="profile-title">{identity.username}</h1>
            <h2 className="profile-subtitle">{identity.id}</h2>
          </div>

          <div className="profile-top-actions">
            <Link to="/analytics" className="profile-admin-button">
              RETURN TO ANALYTICS
            </Link>
          </div>
        </div>

        <div className="profile-hero">
          <div className="profile-avatar-frame">
            {identity.avatar ? (
              <img
                src={identity.avatar}
                alt={identity.username}
                className="profile-avatar"
              />
            ) : (
              <div className="profile-avatar-fallback">
                {identity.username?.charAt(0) || "?"}
              </div>
            )}
          </div>

          <div className="profile-identity-card">
            <div className="profile-identity-line">
              <span className="profile-identity-label">Username</span>
              <span className="profile-identity-value">{identity.username}</span>
            </div>
            <div className="profile-identity-line">
              <span className="profile-identity-label">Discord ID</span>
              <span className="profile-identity-value">{identity.id}</span>
            </div>
            <div className="profile-identity-line">
              <span className="profile-identity-label">House</span>
              <span className="profile-identity-value">{identity.house}</span>
            </div>
            <div className="profile-identity-line">
              <span className="profile-identity-label">XP</span>
              <span className="profile-identity-value">{identity.xp}</span>
            </div>
            <div className="profile-identity-line">
              <span className="profile-identity-label">Merits</span>
              <span className="profile-identity-value">{identity.merits}</span>
            </div>
            <div className="profile-identity-line">
              <span className="profile-identity-label">Strikes</span>
              <span className="profile-identity-value">{identity.strikes}</span>
            </div>
          </div>

          <div className={`dossier-signal-card ${signalClass}`}>
            <div className="dossier-signal-label">Signal Level</div>
            <div className="dossier-signal-level">{signals.level}</div>
            <div className="dossier-signal-score">Score: {signals.score}</div>
          </div>
        </div>

        <div className="profile-grid dossier-summary-grid">
          <DossierStatCard label="Page Views" value={summary.pageViews} />
          <DossierStatCard label="Unique Routes" value={summary.uniqueRoutes} />
          <DossierStatCard label="Right-Click Attempts" value={summary.rightClickAttempts} />
          <DossierStatCard label="Devtools Blocks" value={summary.devtoolsBlocks} />
          <DossierStatCard label="Current Cycle Messages" value={summary.currentCycleMessages} />
          <DossierStatCard label="Archived Cycle Messages" value={summary.archivedCycleMessages} />
        </div>

        <div className="dossier-two-column">
          <div className="analytics-dossier-card">
            <div className="analytics-dossier-card-header">
              <h3>Route Behaviour</h3>
              <p>Most visited routes for this user</p>
            </div>

            <div className="analytics-dossier-scroll">
              <ul className="analytics-dossier-user-list">
                {routes.map((entry) => (
                  <li key={entry.route} className="analytics-dossier-user-row">
                    <div className="analytics-dossier-user-left">
                      <div className="analytics-dossier-user-meta">
                        <span className="analytics-dossier-username">{entry.route}</span>
                      </div>
                    </div>
                    <div className="analytics-dossier-count">{entry.views}</div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="analytics-dossier-card">
            <div className="analytics-dossier-card-header">
              <h3>Message Cycles</h3>
              <p>Current and archived activity totals</p>
            </div>

            <div className="analytics-dossier-scroll">
              <ul className="analytics-dossier-user-list">
                <li className="analytics-dossier-user-row">
                  <div className="analytics-dossier-user-left">
                    <div className="analytics-dossier-user-meta">
                      <span className="analytics-dossier-username">Current Cycle</span>
                    </div>
                  </div>
                  <div className="analytics-dossier-count">{cycles.current}</div>
                </li>

                {cycles.archives.map((entry) => (
                  <li key={entry.label} className="analytics-dossier-user-row">
                    <div className="analytics-dossier-user-left">
                      <div className="analytics-dossier-user-meta">
                        <span className="analytics-dossier-username">{entry.label}</span>
                      </div>
                    </div>
                    <div className="analytics-dossier-count">{entry.count}</div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="analytics-dossier-card dossier-events-card">
          <div className="analytics-dossier-card-header">
            <h3>Recent Event Timeline</h3>
            <p>Last 25 tracked events, newest first</p>
          </div>

          <div className="dossier-events-list">
            {events.map((event, index) => (
              <div className="dossier-event-row" key={`${event.timestamp}-${index}`}>
                <div className="dossier-event-time">{formatTimestamp(event.timestamp)}</div>
                <div className="dossier-event-body">
                  <div className="dossier-event-type">{formatEventLabel(event.type)}</div>
                  <div className="dossier-event-meta">
                    {event.meta?.page ? `Page: ${event.meta.page}` : "Page: unknown"}
                    {event.meta?.key ? ` • Key: ${event.meta.key}` : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDossier;